/**
 * Shared age calculation utility.
 *
 * @param {Date} dob        Date of birth.
 * @param {Date} [refDate]  Reference date for the calculation (defaults to now).
 * @param {number|null} [decimals] Number of decimal places to round to.
 *        • null  → return an INTEGER number of years using Math.floor.
 *        • 0     → return a rounded integer (Math.round).
 *        • >0    → round to the requested decimal places.
 * @returns {number} Age in years. Returns 0 for future DOBs or NaN for invalid input.
 */
export function calculateAge(dob, refDate = new Date(), decimals = null) {
  // Guard against invalid dates.
  if (!(dob instanceof Date) || isNaN(dob.getTime())) {
    return NaN;
  }

  const reference = refDate instanceof Date ? refDate : new Date(refDate);

  // Future dates are considered invalid – treat age as 0.
  if (dob > reference) {
    return 0;
  }

  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const DAYS_PER_YEAR = 365.25; // Accounts for leap years
  const ageYears = ((reference - dob) / MS_PER_DAY) / DAYS_PER_YEAR;

  if (decimals === null) {
    // Integer years (floor) for administrative / validation use-cases.
    return Math.floor(ageYears);
  }

  const factor = Math.pow(10, decimals);
  return Math.round(ageYears * factor) / factor;
} 